import React, { useEffect, useRef, useState } from 'react';
import useLatest from '../hooks/useLatest';
import { WEBGL_HARDWARE_ONLY } from '../util/webgl';
import './ProgressiveFireFill.css';

const vertexSource = `
  attribute vec2 a_position;
  varying vec2 v_uv;
  void main() {
    v_uv = a_position * .5 + .5;
    gl_Position = vec4(a_position, 0., 1.);
  }
`;

// Advected noise flows from left to right; hot ribbons and drifting embers
// are rendered inside the existing fill, independently of its percentage.
const fragmentSource = `
  precision mediump float;
  varying vec2 v_uv;
  uniform vec2 u_resolution;
  uniform float u_time;
  uniform float u_progress;
  uniform vec3 u_color;
  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float noise(vec2 p) {
    vec2 i = floor(p), f = fract(p);
    f = f * f * (3. - 2. * f);
    return mix(mix(hash(i), hash(i + vec2(1., 0.)), f.x),
      mix(hash(i + vec2(0., 1.)), hash(i + vec2(1., 1.)), f.x), f.y);
  }
  float fbm(vec2 p) {
    float value = 0., weight = .5;
    for (int i = 0; i < 4; i++) {
      value += weight * noise(p);
      p = p * 2.03 + vec2(7.1, 3.7);
      weight *= .5;
    }
    return value;
  }
  void main() {
    float aspect = u_resolution.x / max(u_resolution.y, 1.);
    float t = u_time * (.8 + u_progress * .5);
    vec2 p = vec2(v_uv.x * aspect, v_uv.y);
    // Subtract time from X so texture features travel toward the right.
    // Stretch the noise horizontally to form swept flame trails.
    float travel = p.x - t * 2.4;
    float drift = fbm(vec2(travel * .55, p.y * 2.8)) - .5;
    float warpedY = p.y + drift * .4;
    float turbulence = fbm(vec2(travel * .9, warpedY * 5.2));
    float reach = .22 + turbulence * .35;
    float distance = abs(warpedY - .5);
    float flame = 1. - smoothstep(reach - .045, reach + .045, distance);
    float detail = fbm(vec2(travel * 2.7, warpedY * 9.));
    float heat = clamp(.91 - distance * 1.45 - turbulence * .22 + detail * .16, 0., 1.);
    float warm = smoothstep(.35, .75, u_progress);
    vec3 orange = mix(u_color, vec3(1., .38, .018), warm);
    vec3 gold = mix(u_color, vec3(1., .85, .19), .45 + warm * .55);
    vec3 whiteHot = mix(u_color, vec3(1., .98, .72), .62 + warm * .38);
    vec3 fire = mix(u_color * .5, orange, smoothstep(.2, .6, heat));
    fire = mix(fire, gold, smoothstep(.55, .83, heat));
    fire = mix(fire, whiteHot, smoothstep(.82, 1., heat));
    vec2 emberGrid = vec2(travel * 3., p.y * 10.);
    vec2 ember = fract(emberGrid) - .5;
    ember *= vec2(.6, 1.6);
    float sparks = step(.94, hash(floor(emberGrid))) * exp(-dot(ember, ember) * 100.);
    vec3 backdrop = u_color * .12 + vec3(.04, .005, .002);
    vec3 color = mix(backdrop, fire, flame);
    color += gold * sparks * .85;
    color += gold * exp(-abs(distance - reach) * 45.) * flame * .25;
    color += gold * exp(-distance * 10.) * (.08 + detail * .12);
    gl_FragColor = vec4(color, 1.);
  }
`;

function createProgram(gl) {
  const shaders = [];
  const program = gl.createProgram();
  try {
    for (const [type, source] of [[gl.VERTEX_SHADER, vertexSource], [gl.FRAGMENT_SHADER, fragmentSource]]) {
      const shader = gl.createShader(type);
      shaders.push(shader);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error('Fire shader unavailable');
      gl.attachShader(program, shader);
    }
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('Fire program unavailable');
    return program;
  } catch {
    gl.deleteProgram(program);
    return null;
  } finally {
    shaders.forEach(shader => gl.deleteShader(shader));
  }
}

export default function ProgressiveFireFill({ value, color }) {
  const canvasRef = useRef(null);
  const config = useLatest({ value, color });
  const [renderer, setRenderer] = useState('fallback');
  const [generation, setGeneration] = useState(0);
  const enabled = value > 0;
  if (!enabled && renderer !== 'fallback') setRenderer('fallback');

  useEffect(() => {
    if (!enabled) return undefined;
    const canvas = canvasRef.current;
    const gl = canvas.getContext('webgl', { ...WEBGL_HARDWARE_ONLY, alpha: false, antialias: false, depth: false, stencil: false });
    if (!gl) return undefined;
    const program = createProgram(gl);
    if (!program) return undefined;
    gl.useProgram(program);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    const resolution = gl.getUniformLocation(program, 'u_resolution');
    const time = gl.getUniformLocation(program, 'u_time');
    const progress = gl.getUniformLocation(program, 'u_progress');
    const baseColor = gl.getUniformLocation(program, 'u_color');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0, lastPaint = -Infinity, visible = true, lost = false;
    const started = performance.now() - Math.random() * 12000;

    const paint = now => {
      if (lost) return;
      const rgb = config.current.color.match(/[\d.]+/g)?.slice(0, 3).map(channel => Number(channel) / 255) || [1, .3, .02];
      gl.uniform2f(resolution, canvas.width, canvas.height);
      gl.uniform1f(time, reducedMotion.matches ? 4 : (now - started) / 1000);
      gl.uniform1f(progress, Math.max(0, Math.min(100, config.current.value)) / 100);
      gl.uniform3f(baseColor, ...rgb);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };
    const tick = now => {
      frame = 0;
      if (!visible || document.hidden || lost) return;
      if (now - lastPaint >= 1000 / 30) {
        paint(now);
        lastPaint = now;
      }
      if (!reducedMotion.matches) frame = requestAnimationFrame(tick);
    };
    const resume = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      if (visible && !document.hidden && !lost) {
        lastPaint = -Infinity;
        frame = requestAnimationFrame(tick);
      }
    };
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const scale = Math.min(window.devicePixelRatio || 1, 1.5);
      const width = Math.max(1, Math.round(rect.width * scale));
      const height = Math.max(1, Math.round(rect.height * scale));
      if (canvas.width !== width || canvas.height !== height) {
        canvas.width = width;
        canvas.height = height;
        gl.viewport(0, 0, width, height);
      }
      paint(performance.now());
      resume();
    };
    const onLost = event => {
      event.preventDefault();
      lost = true;
      cancelAnimationFrame(frame);
      setRenderer('fallback');
    };
    const onRestored = () => setGeneration(current => current + 1);
    const resizeObserver = new ResizeObserver(resize);
    const visibilityObserver = new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting;
      resume();
    });
    resizeObserver.observe(canvas);
    visibilityObserver.observe(canvas);
    document.addEventListener('visibilitychange', resume);
    reducedMotion.addEventListener('change', resume);
    canvas.addEventListener('webglcontextlost', onLost);
    canvas.addEventListener('webglcontextrestored', onRestored);
    setRenderer('webgl');
    resize();
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      visibilityObserver.disconnect();
      document.removeEventListener('visibilitychange', resume);
      reducedMotion.removeEventListener('change', resume);
      canvas.removeEventListener('webglcontextlost', onLost);
      canvas.removeEventListener('webglcontextrestored', onRestored);
      gl.deleteBuffer(buffer);
      gl.deleteProgram(program);
    };
  }, [generation, enabled, config]);

  return <span className="progressive-fire" data-renderer={renderer} aria-hidden="true" style={{ '--fire-color': color }}>
    <span className="progressive-fire-fallback" />
    <canvas ref={canvasRef} className="progressive-fire-canvas" />
  </span>;
}
